import { useComponentStyle, VariantStyle } from '@altermaind/web/ui';

/**
 * Safely extracts the `subComponents` map from a component's resolved style.
 *
 * @param componentStyle - The resolved {@link VariantStyle} of a component.
 * @returns A record keyed by sub-component name, or an empty object when the
 * component declares no sub-components.
 */
const extractSubComponentStyle = (componentStyle?: VariantStyle) =>
  (componentStyle?.subComponents || {}) as Record<string, VariantStyle>;

/**
 * Resolves the styles of a component's sub-components by merging its default
 * (base) styles with those of a specific variant,
 * basing on the theme style definition.
 *
 * For each sub-component key found in either the default or the variant style,
 * the variant's properties are spread on top of the default's, so variant
 * values override the base ones while base values act as fallbacks. Keys are
 * de-duplicated via a `Set`, ensuring each sub-component is computed only once.
 *
 * @param baseComponentName - The Design System component name whose
 * sub-component styles should be resolved (e.g. `'Switch'`).
 * @param variant - Optional variant name. When provided, its sub-component
 * styles are merged over the default ones; when omitted, only the default
 * styles are returned.
 * @returns A record mapping each sub-component key to its merged
 * {@link VariantStyle}.
 *
 * @example
 * ```tsx
 * const subStyles = useSubComponentStyle('Switch', 'primary');
 * // subStyles.thumb, subStyles.track, ...
 * ```
 */
export const useSubComponentStyle = (baseComponentName: string, variant?: string) => {
  const defaultStyle = useComponentStyle(baseComponentName);
  const variantStyle = useComponentStyle(baseComponentName, variant);

  const defaultSubComponentKeys = Object.keys(extractSubComponentStyle(defaultStyle));
  const variantSubComponentKeys = Object.keys(extractSubComponentStyle(variantStyle));

  const computedSubComponentStyles: Record<string, VariantStyle> = {};

  // Set ensure keys are present only once
  new Set([...defaultSubComponentKeys, ...variantSubComponentKeys]).forEach(subComponentKey => {
    computedSubComponentStyles[subComponentKey] = {
      ...(extractSubComponentStyle(defaultStyle)[subComponentKey] || {}),
      ...(extractSubComponentStyle(variantStyle)[subComponentKey] || {}),
    };
  });

  return computedSubComponentStyles;
};
